import React, { useEffect, useState } from 'react';
import { Pin, PinOff } from 'lucide-react';
import { useAuth } from '../../packages/auth-client';
import { useNavGroups } from '../../components/shell/nav';
import { usePageTitle } from '../../hooks/usePageTitle';
import { FeatureCard, HubHeading } from '../../components/hub/Hub';

interface Tool { to: string; name: string; badge: string; description: string; always?: boolean }

/**
 * Catalogue copy in display order (concept renderTools order), keyed by
 * destination. Every card opens a real route; `always` entries are account
 * pages every signed-in member can open, the rest follow the shell nav.
 */
const CATALOGUE: Tool[] = [
    { to: '/workload', name: 'Workload', badge: 'Delivery', description: 'Open assigned tasks by person, and a heatmap of task counts per day. It measures task counts, not hours or capacity.' },
    { to: '/timeline', name: 'Portfolio timeline', badge: 'Planning', description: 'Every project you can see on one timeline, with its dates and tasks.' },
    { to: '/templates', name: 'Task templates', badge: 'Reusable work', description: 'Your saved task templates: create, delete, or use one to add a task to a project.' },
    { to: '/marketplace', name: 'Marketplace', badge: 'Reusable work', description: 'Built-in project templates: start a new project from a ready-made task list.' },
    { to: '/entities', name: 'Records', badge: 'Context', description: 'Record types, hierarchy, custom fields and relations, linked to projects and tasks.' },
    { to: '/projects', name: 'Reports', badge: 'Reporting', description: "Status breakdown, burndown and status reports live in each project: pick a project, then open its Reports tab." },
    { to: '/profile?tab=notifications', name: 'Notifications & digests', badge: 'Communication', description: 'Your email digest cadence and notification preferences.', always: true },
    { to: '/profile?tab=assistant', name: 'Assistant & calendars', badge: 'Assistance', description: 'Briefings, quiet hours, channels and connected calendars.', always: true },
    { to: '/help', name: 'Help & support', badge: 'Support', description: 'Contact the team, follow your tickets and read the docs.', always: true },
    { to: '/settings', name: 'Workspace & personal settings', badge: 'Administration', description: 'Account, security, AI provider, billing and connection controls.', always: true },
];

export const pinsKey = (userID: string) => `kaziatlas.tools.pins.${userID}`;

// Pins are a per-viewer convenience: storage can be blocked or full, and then
// a pin lasts for this visit only. They only reorder; they never grant access.
export function readPins(key: string): string[] {
    try {
        const v: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
        return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    } catch {
        return [];
    }
}

function writePins(key: string, pins: string[]) {
    try {
        localStorage.setItem(key, JSON.stringify(pins));
    } catch {
        // Not saved; the in-memory pin still applies until the page is left.
    }
}

/** The catalogue entries this viewer may open: the shell nav's rules, plus account pages. */
function useTools(): Tool[] {
    const groups = useNavGroups();
    const allowed = new Set(groups.find((g) => g.id === 'tools')?.items.map((i) => i.to));
    // Reports live in each project, so the entry follows the Projects link.
    if (groups.find((g) => g.id === 'work')?.items.some((i) => i.to === '/projects')) allowed.add('/projects');
    return CATALOGUE.filter((c) => c.always || allowed.has(c.to));
}

export function ToolsPage() {
    usePageTitle('Tools');
    const { user } = useAuth();
    const key = pinsKey(user?.user_id ?? '');
    const tools = useTools();
    const [pins, setPins] = useState<string[]>(() => readPins(key));
    useEffect(() => { setPins(readPins(key)); }, [key]);

    const toggle = (to: string) => {
        const next = pins.includes(to) ? pins.filter((p) => p !== to) : [...pins, to];
        setPins(next);
        writePins(key, next);
    };

    const pinned = tools.filter((t) => pins.includes(t.to));
    const rest = tools.filter((t) => !pins.includes(t.to));

    return (
        <div className="hub-page">
            <HubHeading
                eyebrow="Workspace / Tools"
                title="Keep the tools your projects need."
                description="Planning, delivery, context, and collaboration stay part of the PM system. Pin the ones your team uses most; pins are saved in this browser only."
            />
            {pinned.length > 0 && <ToolSection title="Pinned" tools={pinned} pins={pins} onToggle={toggle} />}
            <ToolSection title={pinned.length > 0 ? 'All other tools' : undefined} tools={rest} pins={pins} onToggle={toggle} />
        </div>
    );
}

function ToolSection({ title, tools, pins, onToggle }: {
    title?: string; tools: Tool[]; pins: string[]; onToggle: (to: string) => void;
}) {
    if (tools.length === 0) return null;
    const id = `tools-${(title ?? 'all').replace(/\s+/g, '-').toLowerCase()}`;
    const grid = (
        <ul className="hub-feature-grid">
            {tools.map((t) => {
                const isPinned = pins.includes(t.to);
                return (
                    <li key={t.to}>
                        <FeatureCard to={t.to} title={t.name} badge={t.badge} description={t.description} label={`Open ${t.name}`} />
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm hub-pin"
                            aria-pressed={isPinned}
                            aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${t.name}`}
                            title={isPinned ? 'Unpin' : 'Pin'}
                            onClick={() => onToggle(t.to)}
                        >
                            {isPinned ? <PinOff size={16} aria-hidden="true" /> : <Pin size={16} aria-hidden="true" />}
                        </button>
                    </li>
                );
            })}
        </ul>
    );
    if (!title) return <section className="hub-section" aria-label="Workspace tools">{grid}</section>;
    return (
        <section className="hub-section" aria-labelledby={id}>
            <h2 id={id} className="hub-section-label">{title}</h2>
            {grid}
        </section>
    );
}
